import { DeepPartial } from 'redux'

import type { AssignmentSummary } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import type {
  Assignment,
  AssignmentResponse,
  AssignmentStatus,
  ExerciseDraftPayload,
  setAssignmentInterface,
} from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'

import { baseApi, RTKQueryTag } from '../../../data/baseApi'

type GetAssignmentStatusesArgs = {
  providerId: string
}

type GetAssignmentsArgs = {
  providerId: string
  patientId: string
}
type GetAssignmentSummaryArgs = {
  assignmentId: string
  providerId: string
  statusOnly?: boolean
}

type GetAssignmentStatusesResponse = Record<string, AssignmentStatus[]>

type PatchAssignmentArgs = {
  id: string
  data: { session_period: number }
}

type UnsetAssignmentArgs = {
  id: string
}

type SetAssignmentResponseArgs = ExerciseDraftPayload

type UpdateAssignmentResponseArgs = DeepPartial<AssignmentResponse>

type DeleteAssignmentResponseArgs = {
  id: string
}

type SetAssignmentDraftArgs = ExerciseDraftPayload

type SetAssignmentDraftResponseToAssignmentArgs = DeepPartial<AssignmentResponse>

const assignmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssignments: builder.query<Assignment[], GetAssignmentsArgs>({
      query: ({ providerId, patientId }) => ({
        method: 'get',
        url: '/lt/v1/assignments',
        params: { provider_id: providerId, patient_id: patientId },
      }),
      providesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    getAssignmentStatuses: builder.query<GetAssignmentStatusesResponse, GetAssignmentStatusesArgs>({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/lt/v1/assignments/bulk/status',
        params: { provider_id: providerId },
      }),
      providesTags: [RTKQueryTag.PERSIST, RTKQueryTag.ASSIGNMENT],
    }),
    getAssignmentSummary: builder.query<AssignmentSummary, GetAssignmentSummaryArgs>({
      query: ({ assignmentId, providerId, statusOnly = false }) => ({
        method: 'get',
        url: `lt/v1/assignments/${assignmentId}/summary`,
        params: { provider_id: providerId, status_only: statusOnly },
      }),
      providesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    setAssignment: builder.mutation<Assignment, setAssignmentInterface>({
      query: (data) => ({
        method: 'post',
        url: '/lt/v1/assignments',
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    updateAssignment: builder.mutation<Assignment, DeepPartial<Assignment>>({
      query: (data) => ({
        method: 'put',
        url: `/lt/v1/assignments/${data.id}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    patchAssignment: builder.mutation<Assignment, PatchAssignmentArgs>({
      query: ({ id, data }) => ({
        method: 'patch',
        url: `/lt/v1/assignments/${id}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    unsetAssignment: builder.mutation<void, UnsetAssignmentArgs>({
      query: ({ id }) => ({
        method: 'delete',
        url: `/lt/v1/assignments/${id}`,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    bulkUnsetAssignment: builder.mutation<void, string[]>({
      query: (data) => ({
        method: 'post',
        url: `/lt/v1/assignments/bulk/delete`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    setAssignmentResponse: builder.mutation<AssignmentResponse, SetAssignmentResponseArgs>({
      query: (data) => ({
        method: 'post',
        url: `/lt/v1/assignmentResponses/${data.assignment_id}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    updateAssignmentResponse: builder.mutation<AssignmentResponse, UpdateAssignmentResponseArgs>({
      query: (data) => ({
        method: 'put',
        url: `/lt/v1/assignmentResponse/${data.id}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    deleteAssignmentResponse: builder.mutation<void, DeleteAssignmentResponseArgs>({
      query: ({ id }) => ({
        method: 'delete',
        url: `/lt/v1/assignmentResponse/${id}`,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    setAssignmentDraft: builder.mutation<AssignmentResponse, SetAssignmentDraftArgs>({
      query: (data) => ({
        method: 'post',
        url: `/lt/v1/unassignedAssignmentResponse`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
    setAssignmentDraftResponseToAssignment: builder.mutation<
      AssignmentResponse,
      SetAssignmentDraftResponseToAssignmentArgs
    >({
      query: (data) => ({
        method: 'put',
        url: `/lt/v1/unassignedAssignmentResponse/${data.id}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT],
    }),
  }),
})

export const {
  useGetAssignmentsQuery,
  useLazyGetAssignmentsQuery,
  useGetAssignmentStatusesQuery,
  useGetAssignmentSummaryQuery,
  useSetAssignmentMutation,
  useUpdateAssignmentMutation,
  usePatchAssignmentMutation,
  useUnsetAssignmentMutation,
  useBulkUnsetAssignmentMutation,
  useSetAssignmentResponseMutation,
  useUpdateAssignmentResponseMutation,
  useDeleteAssignmentResponseMutation,
  useSetAssignmentDraftMutation,
  useSetAssignmentDraftResponseToAssignmentMutation,
} = assignmentsApi
export default assignmentsApi
